import {
  FloatingFormItem,
  InputDecimal,
  InputText,
} from '@/components/_common';
import { InvoiceItemFormValues } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Card, Col, Form, FormInstance, Input, Row } from 'antd';
import React from 'react';

interface Props {
  form: FormInstance<InvoiceItemFormValues>;
}

const NoteItemsFuelTabsForm: React.FC<Props> = ({ form }) => {
  const ruleRequired = useDefaultEmptyFieldError();
  const formValues = Form.useWatch([], form);

  const hasCideValues =
    !!formValues?.fuel?.cideValue ||
    !!formValues?.fuel?.cideTaxRate ||
    !!formValues?.fuel?.cideCalculationBase;

  const hasClosingValues =
    !!formValues?.fuel?.tankNumber ||
    !!formValues?.fuel?.pumpNumber ||
    !!formValues?.fuel?.nozzleNumber ||
    !!formValues?.fuel?.endValue ||
    !!formValues?.fuel?.startValue;

  return (
    <Form form={form} style={{ minHeight: 300 }}>
      <Card
        title="Dados do combustível"
        type="inner"
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24} md={12} lg={6} xxl={10}>
            <FloatingFormItem
              label="Descrição do produto"
              name={['fuel', 'anpDescription']}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem label="GLP (%)" name={['fuel', 'glpPercentage']}>
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={5}>
            <FloatingFormItem
              label="Gás natural nacional (%)"
              name={['fuel', 'gnnPercentage']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={5}>
            <FloatingFormItem
              label="Gás natural importado (%)"
              name={['fuel', 'gniPercentage']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Valor de partida"
              name={['fuel', 'startingValue']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={5}>
            <FloatingFormItem
              label="Código ANP"
              name={['fuel', 'anpCode']}
              rules={[
                { type: 'string', min: 9 },
                { type: 'string', max: 9 },
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={5}>
            <FloatingFormItem
              label="CODIF"
              name={['fuel', 'authorizationCode']}
              rules={[
                { type: 'string', min: 1 },
                { type: 'string', max: 27 },
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={5}>
            <FloatingFormItem
              label="Qtde. comb. faturada temp. ambiente"
              name={['fuel', 'ambientTemperatureBilling']}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={5}>
            <FloatingFormItem
              label="UF consumo"
              name={['fuel', 'consumptionState']}
            >
              <InputText maxLength={2} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="CIDE" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Base de cálculo"
              name={['fuel', 'cideCalculationBase']}
              rules={hasCideValues ? [ruleRequired] : []}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Alíquota"
              name={['fuel', 'cideTaxRate']}
              rules={hasCideValues ? [ruleRequired] : []}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Valor"
              name={['fuel', 'cideValue']}
              rules={hasCideValues ? [ruleRequired] : []}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="Encerrante" type="inner">
        <Row gutter={[16, 16]}>
          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Valor início"
              name={['fuel', 'startValue']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Valor final"
              name={['fuel', 'endValue']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Núm. de identificação do bico"
              name={['fuel', 'nozzleNumber']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Núm. de identificação do tanque"
              name={['fuel', 'tankNumber']}
              rules={hasClosingValues ? [ruleRequired] : []}
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label="Núm. de identificação da bomba"
              name={['fuel', 'pumpNumber']}
            >
              <InputText onlyNumber />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>
    </Form>
  );
};

export default NoteItemsFuelTabsForm;
